<template>
  <div class="p-4 pb-20">
    <!-- Header -->
    <div class="flex items-center justify-between mb-4">
      <h2 class="text-xl font-bold text-gray-900">Device Management</h2>
      <button class="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors">
        + Add Device
      </button>
    </div>

    <!-- Search and Filter -->
    <div class="bg-white rounded-lg p-3 shadow-sm border border-gray-200 mb-4">
      <div class="flex items-center space-x-2 mb-3">
        <div class="flex-1 relative">
          <input 
            type="text" 
            placeholder="Search devices..."
            class="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
          >
          <span class="absolute left-3 top-2.5 text-gray-400">🔍</span>
        </div>
        <button class="p-2 border border-gray-300 rounded-lg hover:bg-gray-50">
          <span>🔽</span>
        </button>
      </div>
      
      <div class="flex space-x-2">
        <span class="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs">All (12)</span>
        <span class="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">Active (8)</span>
        <span class="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">Maintenance (2)</span>
        <span class="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">Offline (2)</span>
      </div>
    </div>

    <!-- Device List -->
    <div class="space-y-3">
      <div v-for="device in devices" :key="device.id" class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div class="p-4">
          <div class="flex items-start justify-between mb-3">
            <div class="flex items-center space-x-3">
              <div class="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <span class="text-xl">🚁</span>
              </div>
              <div>
                <h3 class="font-semibold text-gray-900">{{ device.name }}</h3>
                <p class="text-sm text-gray-500">{{ device.model }}</p>
                <p class="text-xs text-gray-400">ID: {{ device.id }}</p>
              </div>
            </div>
            <div class="flex flex-col items-end">
              <span :class="getStatusClass(device.status)" class="px-2 py-1 rounded-full text-xs font-medium">
                {{ device.status }}
              </span>
              <span class="text-xs text-gray-400 mt-1">{{ device.lastUpdate }}</span>
            </div>
          </div>

          <div class="grid grid-cols-3 gap-3 mb-3">
            <div class="text-center">
              <p class="text-lg font-semibold text-gray-900">{{ device.flightHours }}</p>
              <p class="text-xs text-gray-500">Flight Hours</p>
            </div>
            <div class="text-center">
              <p class="text-lg font-semibold text-gray-900">{{ device.hectares }}</p>
              <p class="text-xs text-gray-500">Hectares</p>
            </div>
            <div class="text-center">
              <p class="text-lg font-semibold text-gray-900">{{ device.battery }}%</p>
              <p class="text-xs text-gray-500">Battery</p>
            </div>
          </div>

          <div class="flex items-center justify-between pt-3 border-t border-gray-100">
            <div class="flex items-center space-x-2 text-sm text-gray-600">
              <span>📍</span>
              <span>{{ device.location }}</span>
            </div>
            <div class="flex space-x-2">
              <button class="px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 transition-colors">
                Track
              </button>
              <button class="px-3 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-700 transition-colors">
                Details
              </button>
            </div>
          </div>
        </div>

        <!-- Device Actions -->
        <div class="bg-gray-50 px-4 py-2 flex justify-between items-center">
          <div class="flex items-center space-x-4 text-xs text-gray-600">
            <span>Operator: {{ device.operator }}</span>
            <span>Company: {{ device.company }}</span>
          </div>
          <div class="flex space-x-2">
            <button class="text-green-600 hover:text-green-700">
              <span class="text-sm">⚙️</span>
            </button>
            <button class="text-blue-600 hover:text-blue-700">
              <span class="text-sm">📊</span>
            </button>
            <button class="text-red-600 hover:text-red-700">
              <span class="text-sm">🗑️</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Floating Action Button -->
    <button class="fixed bottom-20 right-4 w-14 h-14 bg-green-600 text-white rounded-full shadow-lg flex items-center justify-center hover:bg-green-700 transition-colors z-30">
      <span class="text-xl">+</span>
    </button>
  </div>
</template>

<script setup>
import { ref } from 'vue'

const devices = ref([
  {
    id: 'BY-213322010133',
    name: 'DJI Agras MG-1',
    model: 'MG-1P',
    status: 'Active',
    lastUpdate: '2 min ago',
    flightHours: '234',
    hectares: '1,245',
    battery: '85',
    location: 'Field A-3',
    operator: 'TEST01',
    company: 'Kasetgeny'
  },
  {
    id: 'BY-213322010134',
    name: 'DJI Agras MG-1',
    model: 'MG-1P',
    status: 'Flying',
    lastUpdate: 'Now',
    flightHours: '189',
    hectares: '987',
    battery: '67',
    location: 'Field B-2',
    operator: 'scrasil',
    company: 'Kasetgeny'
  },
  {
    id: 'BY-213322010135',
    name: 'DJI Agras MG-1',
    model: 'MG-1P',
    status: 'Maintenance',
    lastUpdate: '1 hour ago',
    flightHours: '412',
    hectares: '2,156',
    battery: '45',
    location: 'Hangar',
    operator: 'TEST01',
    company: 'Kasetgeny'
  },
  {
    id: 'BY-213322010136',
    name: 'XAG P30',
    model: 'P30',
    status: 'Active',
    lastUpdate: '15 min ago',
    flightHours: '156',
    hectares: '823',
    battery: '92',
    location: 'Field C-1',
    operator: 'scrasil',
    company: 'Kasetgeny'
  }
])

const getStatusClass = (status) => {
  const classes = {
    'Active': 'bg-green-100 text-green-800',
    'Flying': 'bg-blue-100 text-blue-800',
    'Maintenance': 'bg-yellow-100 text-yellow-800',
    'Offline': 'bg-red-100 text-red-800'
  }
  return classes[status] || 'bg-gray-100 text-gray-800'
}
</script>
